/**
 * IngestionAdminTab — Self-contained tab component for the ingestion
 * pipeline admin UI.  Renders inside RagAdminPage as a TabPanel.
 *
 * Features:
 *   - Stats cards (total, needs attention, by-state breakdown)
 *   - Filterable/paginated record list
 *   - Detail modal with audit timeline
 *   - Admin actions: retry, classify, reject
 */

import { useCallback, useEffect, useState } from 'react';
import { adminService } from '../../services/admin';
import { Card, StatusBadge } from '../common';
import { formatFileSize } from '../../utils/dateFormatters';
import styles from './IngestionAdminTab.module.css';
import type {
  IngestionRecord,
  IngestionRecordDetail,
  IngestionStats,
  IngestionCategory,
  IngestionState,
} from '../../types/api';

// ── Helpers ────────────────────────────────────────────────────────────────

const STATE_VARIANT: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  completed: 'success',
  uploaded: 'info',
  parsing: 'info',
  parsed: 'info',
  classifying: 'info',
  classified: 'info',
  processing: 'info',
  parse_failed: 'error',
  process_failed: 'error',
  unclassifiable: 'warning',
  new_category: 'warning',
  needs_module: 'warning',
  rejected: 'neutral',
};

const PROCESSING_STATES: IngestionState[] = ['parsing', 'classifying', 'processing'];

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ── Component ──────────────────────────────────────────────────────────────

export function IngestionAdminTab() {
  // Stats
  const [stats, setStats] = useState<IngestionStats | null>(null);

  // Records list
  const [records, setRecords] = useState<IngestionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  // Filters
  const [stateFilter, setStateFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // Detail modal
  const [selectedRecord, setSelectedRecord] = useState<IngestionRecordDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Categories (for classify action)
  const [categories, setCategories] = useState<IngestionCategory[]>([]);

  // Action feedback
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Loading
  const [isLoading, setIsLoading] = useState(true);

  // ── Data fetching ──────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    try {
      const data = await adminService.getIngestionStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch ingestion stats:', err);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const data = await adminService.getIngestionRecords({
        limit,
        offset,
        state: stateFilter || undefined,
        source: sourceFilter || undefined,
        search: searchText || undefined,
      });
      setRecords(data.records);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch ingestion records:', err);
    }
  }, [offset, stateFilter, sourceFilter, searchText]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await adminService.getIngestionCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setIsLoading(true);
      await Promise.all([fetchStats(), fetchRecords(), fetchCategories()]);
      setIsLoading(false);
    };
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // ── Actions ────────────────────────────────────────────────────────────

  const openDetail = async (recordId: string) => {
    setIsLoadingDetail(true);
    try {
      const detail = await adminService.getIngestionRecord(recordId);
      setSelectedRecord(detail);
    } catch (err) {
      console.error('Failed to load record detail:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleRetry = async (recordId: string) => {
    setActionInProgress(recordId);
    try {
      const result = await adminService.retryIngestionRecord(recordId);
      setActionMessage({ type: 'success', text: result.message });
      await Promise.all([fetchStats(), fetchRecords()]);
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to retry record' });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleClassify = async (recordId: string, categoryId: string) => {
    setActionInProgress(recordId);
    try {
      const result = await adminService.classifyIngestionRecord(recordId, categoryId);
      setActionMessage({ type: 'success', text: result.message });
      setSelectedRecord(null);
      await Promise.all([fetchStats(), fetchRecords()]);
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to classify record' });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleReject = async (recordId: string, reason: string) => {
    setActionInProgress(recordId);
    try {
      const result = await adminService.rejectIngestionRecord(recordId, reason);
      setActionMessage({ type: 'success', text: result.message });
      setSelectedRecord(null);
      await Promise.all([fetchStats(), fetchRecords()]);
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to reject record' });
    } finally {
      setActionInProgress(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading ingestion pipeline...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Action message toast */}
      {actionMessage && (
        <div className={actionMessage.type === 'success' ? styles.successToast : styles.errorToast}>
          {actionMessage.text}
          <button className={styles.dismissBtn} onClick={() => setActionMessage(null)}>
            &times;
          </button>
        </div>
      )}

      {/* Stats cards */}
      {stats && <StatsCards stats={stats} onStateClick={(s) => { setStateFilter(s); setOffset(0); }} />}

      {/* Filters */}
      <div className={styles.filterBar}>
        <select
          className={styles.filterSelect}
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value); setOffset(0); }}
        >
          <option value="">All States</option>
          <option value="uploaded">Uploaded</option>
          <option value="parsing">Parsing</option>
          <option value="parsed">Parsed</option>
          <option value="classifying">Classifying</option>
          <option value="classified">Classified</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <optgroup label="Needs Attention">
            <option value="parse_failed">Parse Failed</option>
            <option value="unclassifiable">Unclassifiable</option>
            <option value="new_category">New Category</option>
            <option value="needs_module">Needs Module</option>
            <option value="process_failed">Process Failed</option>
            <option value="rejected">Rejected</option>
          </optgroup>
        </select>

        <select
          className={styles.filterSelect}
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setOffset(0); }}
        >
          <option value="">All Sources</option>
          <option value="upload">Upload</option>
          <option value="ftp">FTP</option>
          <option value="email">Email</option>
          <option value="api">API</option>
          <option value="website">Website</option>
          <option value="scheduled">Scheduled</option>
        </select>

        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search filename..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setOffset(0); }}
        />
      </div>

      {/* Records table */}
      <Card>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Source</th>
                <th>State</th>
                <th>Category</th>
                <th>Size</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.emptyRow}>
                    No records found
                  </td>
                </tr>
              ) : (
                records.map((rec) => (
                  <tr key={rec.id} onClick={() => openDetail(rec.id)}>
                    <td>
                      <div className={styles.filename}>
                        {rec.original_filename || rec.source_reference || rec.id.slice(0, 8)}
                      </div>
                      {rec.source_reference && rec.original_filename && (
                        <div className={styles.sourceRef}>{rec.source_reference}</div>
                      )}
                    </td>
                    <td>
                      <span className={styles.sourceBadge}>{rec.source}</span>
                    </td>
                    <td>
                      <StatusBadge
                        variant={STATE_VARIANT[rec.state] || 'neutral'}
                        pulse={PROCESSING_STATES.includes(rec.state)}
                      >
                        {rec.state.replace(/_/g, ' ')}
                      </StatusBadge>
                    </td>
                    <td>{rec.category_id || '-'}</td>
                    <td>{rec.file_size !== null && rec.file_size !== undefined ? formatFileSize(rec.file_size) : '-'}</td>
                    <td className={styles.timeCell}>{timeAgo(rec.updated_at)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {(rec.state === 'parse_failed' || rec.state === 'process_failed') && (
                        <button
                          className={styles.actionBtn}
                          disabled={actionInProgress === rec.id}
                          onClick={() => handleRetry(rec.id)}
                        >
                          Retry
                        </button>
                      )}
                      {rec.error_message && (
                        <span className={styles.errorHint} title={rec.error_message}>
                          &#9888;
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Showing {Math.min(offset + 1, total)}-{Math.min(offset + limit, total)} of {total}
          </span>
          <div className={styles.paginationBtns}>
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              Previous
            </button>
            <button
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            >
              Next
            </button>
          </div>
        </div>
      </Card>

      {/* Detail modal */}
      {isLoadingDetail && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.spinner} />
            <p>Loading record...</p>
          </div>
        </div>
      )}

      {selectedRecord && !isLoadingDetail && (
        <RecordDetailModal
          record={selectedRecord}
          categories={categories}
          actionInProgress={actionInProgress}
          onClose={() => setSelectedRecord(null)}
          onRetry={handleRetry}
          onClassify={handleClassify}
          onReject={handleReject}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatsCards({
  stats,
  onStateClick,
}: {
  stats: IngestionStats;
  onStateClick: (state: string) => void;
}) {
  const errorStates = ['parse_failed', 'process_failed', 'unclassifiable', 'new_category', 'needs_module'];
  return (
    <div className={styles.statsGrid}>
      <div className={styles.statCard} onClick={() => onStateClick('')}>
        <div className={styles.statLabel}>Total</div>
        <div className={styles.statValue}>{stats.total}</div>
      </div>
      <div
        className={`${styles.statCard} ${stats.needs_attention > 0 ? styles.statAttention : ''}`}
        onClick={() => onStateClick('')}
      >
        <div className={styles.statLabel}>Needs Attention</div>
        <div className={styles.statValue}>{stats.needs_attention}</div>
      </div>
      <div className={styles.statCard} onClick={() => onStateClick('completed')}>
        <div className={styles.statLabel}>Completed</div>
        <div className={styles.statValue}>{stats.by_state['completed'] || 0}</div>
      </div>

      {/* Error state breakdown (only show if non-zero) */}
      {errorStates.map((s) =>
        (stats.by_state[s] || 0) > 0 ? (
          <div key={s} className={styles.statCard} onClick={() => onStateClick(s)}>
            <div className={styles.statLabel}>{s.replace(/_/g, ' ')}</div>
            <div className={`${styles.statValue} ${styles.statError}`}>
              {stats.by_state[s]}
            </div>
          </div>
        ) : null
      )}
    </div>
  );
}

function RecordDetailModal({
  record,
  categories,
  actionInProgress,
  onClose,
  onRetry,
  onClassify,
  onReject,
}: {
  record: IngestionRecordDetail;
  categories: IngestionCategory[];
  actionInProgress: string | null;
  onClose: () => void;
  onRetry: (id: string) => void;
  onClassify: (id: string, categoryId: string) => void;
  onReject: (id: string, reason: string) => void;
}) {
  const [classifyCategory, setClassifyCategory] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const canRetry = record.state === 'parse_failed' || record.state === 'process_failed';
  const canClassify = ['unclassifiable', 'new_category', 'needs_module'].includes(record.state);
  const canReject = [
    'unclassifiable', 'new_category', 'needs_module',
    'parse_failed', 'process_failed',
  ].includes(record.state);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{record.original_filename || record.id.slice(0, 12)}</h3>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <div className={styles.modalBody}>
          {/* Properties grid */}
          <div className={styles.propsGrid}>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>State</span>
              <StatusBadge variant={STATE_VARIANT[record.state] || 'neutral'}>
                {record.state.replace(/_/g, ' ')}
              </StatusBadge>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Source</span>
              <span>{record.source}</span>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Category</span>
              <span>{record.category_id || '-'}</span>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Confidence</span>
              <span>
                {record.classification_confidence !== null
                  ? `${(record.classification_confidence * 100).toFixed(0)}%`
                  : '-'}
              </span>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Method</span>
              <span>{record.classification_method || '-'}</span>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Size</span>
              <span>{record.file_size !== null && record.file_size !== undefined ? formatFileSize(record.file_size) : '-'}</span>
            </div>
            <div className={styles.propItem}>
              <span className={styles.propLabel}>Created</span>
              <span>{new Date(record.created_at).toLocaleString()}</span>
            </div>
            {record.source_reference && (
              <div className={`${styles.propItem} ${styles.propWide}`}>
                <span className={styles.propLabel}>Source Ref</span>
                <span className={styles.sourceRefDetail}>{record.source_reference}</span>
              </div>
            )}
          </div>

          {/* Error message */}
          {record.error_message && (
            <div className={styles.errorBox}>
              <strong>Error ({record.error_stage || 'unknown'}):</strong> {record.error_message}
            </div>
          )}

          {/* Classification reasoning */}
          {record.classification_reasoning && (
            <div className={styles.reasoningBox}>
              <strong>Classification Reasoning:</strong>
              <p>{record.classification_reasoning}</p>
            </div>
          )}

          {/* New category suggestion */}
          {record.state === 'new_category' && record.suggested_category_name && (
            <div className={styles.newCategoryBox}>
              <strong>Suggested Category:</strong> {record.suggested_category_name}
              {record.suggested_category_description && (
                <p>{record.suggested_category_description}</p>
              )}
            </div>
          )}

          {/* Text excerpt */}
          {record.text_excerpt && (
            <div className={styles.excerptBox}>
              <strong>Text Excerpt:</strong>
              <pre className={styles.excerptText}>{record.text_excerpt}</pre>
            </div>
          )}

          {/* Admin actions */}
          {(canRetry || canClassify || canReject) && (
            <div className={styles.actionsSection}>
              <h4>Admin Actions</h4>

              {canRetry && (
                <button
                  className={styles.retryBtn}
                  disabled={actionInProgress === record.id}
                  onClick={() => onRetry(record.id)}
                >
                  Retry
                </button>
              )}

              {canClassify && (
                <div className={styles.classifyRow}>
                  <select
                    value={classifyCategory}
                    onChange={(e) => setClassifyCategory(e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">Select category...</option>
                    {categories
                      .filter((c) => c.is_active)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.id} — {c.name}
                        </option>
                      ))}
                  </select>
                  <button
                    className={styles.classifyBtn}
                    disabled={!classifyCategory || actionInProgress === record.id}
                    onClick={() => onClassify(record.id, classifyCategory)}
                  >
                    Classify
                  </button>
                </div>
              )}

              {canReject && (
                <div className={styles.rejectRow}>
                  <input
                    type="text"
                    placeholder="Rejection reason..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className={styles.searchInput}
                  />
                  <button
                    className={styles.rejectBtn}
                    disabled={!rejectReason.trim() || actionInProgress === record.id}
                    onClick={() => onReject(record.id, rejectReason)}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Audit timeline */}
          {record.audit_log.length > 0 && (
            <div className={styles.auditSection}>
              <h4>Audit Timeline</h4>
              <div className={styles.timeline}>
                {record.audit_log.map((entry, idx) => (
                  <div key={idx} className={styles.timelineItem}>
                    <div className={styles.timelineDot} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineHeader}>
                        <span className={styles.timelineEvent}>{entry.event_type}</span>
                        <span className={styles.timelineTime}>
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className={styles.timelineReason}>{entry.reason}</div>
                      {entry.from_state && entry.to_state && (
                        <div className={styles.timelineTransition}>
                          {entry.from_state} &rarr; {entry.to_state}
                        </div>
                      )}
                      {entry.duration_ms !== null && (
                        <span className={styles.timelineDuration}>{entry.duration_ms}ms</span>
                      )}
                      <div className={styles.timelineActor}>{entry.actor}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default IngestionAdminTab;
